import React, { useCallback, useEffect, useState } from 'react'
import {
  BlockStack,
  Box,
  Grid,
  Text,
  Filters,
  ChoiceList,
  EmptyState,
  SkeletonBodyText,
  SkeletonDisplayText,
  Card,
} from '@shopify/polaris'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useSearchParams } from 'react-router-dom'

import { AnalyticsCard } from '../../common/AnalyticsCard'
import { WIDGET_TYPES } from '../../../../constants/widgets/index.js'
import { getSectionAnalytics } from '../../../../apis/analytics.js'
import { useShopDetails } from '../../../../hooks/useShopDetails.js'
import {
  currencyFormatter,
  numberFormater,
  percentageFormatter,
  simplePercentageFormatter,
} from '../../../../utils/formater'

function AnalyticsSections({ durationFilter: _durationFilter }) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const durationFilter = {
    start: searchParams.get('start')
      ? new Date(searchParams.get('start'))
      : _durationFilter.start,
    end: searchParams.get('end')
      ? new Date(searchParams.get('end'))
      : _durationFilter.end,
  }

  const { data: shopData, isLoading: shopLoading } = useShopDetails()
  const shopSections = shopData?.sections ?? []

  // Initialize section filter state - start with empty array instead of undefined for consistency
  const [sectionFilter, setSectionFilter] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)

  // Auto-select first section when shop data loads
  useEffect(() => {
    if (!shopLoading && shopSections.length > 0 && !isInitialized) {
      // Auto-select the first section
      const firstSectionId = String(shopSections[0].id)
      setSectionFilter([firstSectionId])
      setIsInitialized(true)
    }
  }, [shopLoading, shopSections.length, isInitialized])

  const handleSectionFilterChange = useCallback(
    (value) => setSectionFilter(value),
    [],
  )

  const handleSectionFilterRemove = useCallback(
    () => setSectionFilter([]),
    [],
  )

  const handleFiltersClearAll = useCallback(() => {
    handleSectionFilterRemove()
  }, [handleSectionFilterRemove])

  // Create choices for section filter - actual section instances
  const sectionChoices = shopSections.map((section) => ({
    label: `${section.title || `Section #${section.id}`} (ID: ${section.id}) - ${WIDGET_TYPES[section.type]?.label || section.type}`,
    value: String(section.id),
  }))

  const filters = [
    {
      key: 'section',
      label: t('Analytics.Sections.Filter.section'),
      filter: (
        <ChoiceList
          title="Section"
          titleHidden
          disabled={shopLoading}
          choices={sectionChoices}
          selected={sectionFilter || []}
          onChange={handleSectionFilterChange}
          allowMultiple
        />
      ),
      shortcut: true,
    }
  ]

  // Always show applied filters when a section is selected
  const appliedFilters = React.useMemo(() => {
    const filters = []
    if (sectionFilter && sectionFilter.length > 0) {
      const key = 'section'
      filters.push({
        key,
        label: disambiguateLabel(key, sectionFilter, shopSections),
        onRemove: handleSectionFilterRemove,
      })
    }
    return filters
  }, [sectionFilter, shopSections, handleSectionFilterRemove])

  // Get selected section IDs
  const selectedSectionIds = (sectionFilter ?? [])
    .map((id) => parseInt(id))
    .filter((id) => !isNaN(id))
  
  // Check if any section is selected
  const hasSectionSelected = selectedSectionIds.length > 0

  // Query for section analytics data using new API
  const analyticsQuery = useQuery({
    queryKey: ['section-analytics', selectedSectionIds, durationFilter],
    queryFn: async () => {
      const { data, error } = await getSectionAnalytics({
        sectionIds: selectedSectionIds,
        dateGte: new Date(durationFilter.start.setHours(0, 0, 0, 0)),
        dateLte: new Date(durationFilter.end.setHours(23, 59, 59, 999)),
      })
      if (error) {
        return Promise.reject(error)
      }
      return data
    },
    enabled: hasSectionSelected && durationFilter.end - durationFilter.start > 24 * 60 * 60 * 1000,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 5,
    keepPreviousData: true,
  })

  const data = analyticsQuery.data || {}
  const aggregates = data.dailyMetrics || []
  const metrics = data.metrics || {}

  // Create analytics cards based on data
  const cards = getCards(aggregates, metrics, t, analyticsQuery.isLoading)

  // Create skeleton loader component
  const SkeletonLoader = () => (
    <Grid
      gap={{
        xs: 'var(--p-space-400)',
        sm: 'var(--p-space-400)',
        md: 'var(--p-space-400)',
        lg: 'var(--p-space-400)',
        xl: 'var(--p-space-400)',
      }}
      columns={{
        xs: 1,
        sm: 1,
        md: 2,
        lg: 3,
        xl: 3,
      }}
    >
      {[1, 2, 3, 4, 5, 6, 7].map((index) => (
        <Card key={index}>
          <Box padding="400">
            <BlockStack gap="200">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={3} />
            </BlockStack>
          </Box>
        </Card>
      ))}
    </Grid>
  )

  // Check for loading states
  const isLoadingData = shopLoading || (hasSectionSelected && analyticsQuery.isLoading && !analyticsQuery.data)

  // Get selected section details for display
  const selectedSectionDetails = selectedSectionIds.map(id => {
    const section = shopSections.find(s => s.id === id)
    return section ? `${section.title || `Section #${section.id}`} (ID: ${section.id})` : `ID: ${id}`
  })

  return (
    <BlockStack gap={'400'}>
      <Filters
        key={`filters-${sectionFilter.join(',')}`}
        filters={filters}
        appliedFilters={appliedFilters}
        onClearAll={handleFiltersClearAll}
        hideQueryField
        disableFilters={shopLoading}
      />

      {/* Show which sections are selected */}
      {hasSectionSelected && selectedSectionDetails.length > 0 && (
        <Box>
          <Text variant="bodyMd" tone="subdued">
            {t('Analytics.Sections.showingAnalyticsFor')}: <Text as="span" fontWeight="semibold">{selectedSectionDetails.join(', ')}</Text>
          </Text>
        </Box>
      )}

      {/* Show loading skeleton when loading shop data or analytics */}
      {isLoadingData ? (
        <SkeletonLoader />
      ) : shopSections.length === 0 ? (
        // No sections exist in the shop
        <Box padding={'2000'}>
          <EmptyState
            heading="No sections found"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Your shop doesn't have any sections configured yet. Please create sections first to view analytics.</p>
          </EmptyState>
        </Box>
      ) : !hasSectionSelected ? (
        // Sections exist but none selected (this should rarely happen now with auto-select)
        <Box padding={'2000'}>
          <EmptyState
            heading="Select Section to show stats"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please select one or more sections from the filter above to view their analytics.</p>
          </EmptyState>
        </Box>
      ) : (
        // Show analytics cards
        <Grid
          gap={{
            xs: 'var(--p-space-400)',
            sm: 'var(--p-space-400)',
            md: 'var(--p-space-400)',
            lg: 'var(--p-space-400)',
            xl: 'var(--p-space-400)',
          }}
          columns={{
            xs: 1,
            sm: 1,
            md: 2,
            lg: 3,
            xl: 3,
          }}
        >
          {cards.map((card, index) => (
            <AnalyticsCard key={index} {...card} />
          ))}
        </Grid>
      )}
      <Box padding={'400'} />
    </BlockStack>
  )

  function getCards(aggregates, metrics, t, loading) {
    const graphLabels = aggregates?.map((d) =>
      format(new Date(d.date), 'MMM dd'),
    )

    // Calculate total metrics from daily aggregates
    const totalViewed = metrics.sessionsWithRecommendations || 0
    const totalInteractions = metrics.sessionsWithInteractions || 0
    const totalPurchases = metrics.checkoutCountWithRecommendationPurchases || 0
    const totalRevenue = metrics.totalAmountWithRecommendationPurchases || 0

    // Use conversion rates from metrics (already as decimals, percentageFormatter will multiply by 100)
    const viewRate = metrics.recommendationViewRate || 0
    const interactionRate = metrics.recommendationCtr || 0
    const purchaseRate = metrics.recommendationConversionRate || 0
    const storeConversionRate = metrics.conversionRate || 0

    return [
      {
        label: t('Analytics.Sections.Cards.interactions.title'),
        value: numberFormater(metrics.totalSessions || 0),
        type: 'stats',
        loading,
        tableRows: [
          [
            {
              label: t('Analytics.Sections.Cards.interactions.totalSessions'),
              value: numberFormater(metrics.totalSessions || 0) + ' sessions',
            },
            {
              label: null,
              value: '100%',
            },
          ],
          [
            {
              label: t('Analytics.Sections.Cards.interactions.viewed'),
              value: numberFormater(totalViewed) + ' sessions',
            },
            {
              label: null,
              value: percentageFormatter(viewRate),
            },
          ],
          [
            {
              label: t('Analytics.Sections.Cards.interactions.clicked'),
              value: numberFormater(totalInteractions) + ' sessions',
            },
            {
              label: null,
              value: percentageFormatter(totalInteractions / (metrics.totalSessions || 1)),
            },
          ],
          [
            {
              label: t('Analytics.Sections.Cards.interactions.addToCart'),
              value: numberFormater(totalPurchases) + ' purchases',
            },
            {
              label: null,
              value: percentageFormatter(totalPurchases / (metrics.totalSessions || 1)),
            },
          ],
        ],
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">
                {t('Analytics.Sections.Cards.interactions.title')}
              </Text>
              <Text variant="bodySm">
                {t('Analytics.Sections.Cards.interactions.description')}
              </Text>
            </BlockStack>
          </Box>
        ),
      },
      {
        label: t('Analytics.Sections.Cards.purchaseRateOverTime.label'),
        value: percentageFormatter(purchaseRate),
        total: null,
        type: 'daily',
        graphLabels,
        graphDataSets: [
          {
            label: t('Analytics.Sections.Cards.purchaseRateOverTime.storeConversion'),
            data: aggregates?.map((d) => (d.conversionRate || 0)),
          },
          {
            label: t('Analytics.Sections.Cards.purchaseRateOverTime.purchaseRate'),
            data: aggregates?.map((d) => (d.recommendationConversionRate || 0)),
          },
        ],
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">
                {t('Analytics.Sections.Cards.purchaseRateOverTime.label')}
              </Text>
              <Text variant="bodySm">
                {t('Analytics.Sections.Cards.purchaseRateOverTime.description')}
              </Text>
            </BlockStack>
          </Box>
        ),
        formatter: percentageFormatter,
        loading,
      },
      {
        label: t('Analytics.Sections.Cards.engagementOverTime.label'),
        value: percentageFormatter(interactionRate),
        total: null,
        type: 'daily',
        graphLabels,
        graphDataSets: [
          {
            label: t('Analytics.Sections.Cards.engagementOverTime.viewRate'),
            data: aggregates?.map((d) => (d.recommendationViewRate || 0)),
          },
          {
            label: t('Analytics.Sections.Cards.engagementOverTime.clickRate'),
            data: aggregates?.map((d) => (d.recommendationCtr || 0)),
          },
        ],
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">
                {t('Analytics.Sections.Cards.engagementOverTime.label')}
              </Text>
              <Text variant="bodySm">
                {t('Analytics.Sections.Cards.engagementOverTime.description')}
              </Text>
            </BlockStack>
          </Box>
        ),
        formatter: percentageFormatter,
        loading,
      },
      {
        label: t('Analytics.Sections.Cards.revenueAttribution.title'),
        description: t('Analytics.Sections.Cards.revenueAttribution.description'),
        value: currencyFormatter(
          window.shopify.data.shop.currency,
          totalRevenue || metrics.totalAmountWithRecommendationPurchases || 0,
        ),
        formatter: (val) =>
          currencyFormatter(window.shopify.data.shop.currency, val),
        type: 'daily',
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">
                {t('Analytics.Sections.Cards.revenueAttribution.title')}
              </Text>
              <Text variant="bodySm">
                {t('Analytics.Sections.Cards.revenueAttribution.description')}
              </Text>
            </BlockStack>
          </Box>
        ),
        graphLabels,
        graphDataSets: [
          {
            label: t('Analytics.Sections.Cards.revenueAttribution.revenue'),
            data: aggregates?.map((d) => d.totalAmountWithRecommendationPurchases || 0),
          },
        ],
        loading,
      },
      {
        label: t('Analytics.Sections.Cards.revenueContribution.label'),
        description: t('Analytics.Sections.Cards.revenueContribution.description'),
        value: percentageFormatter(
          metrics.recommendationRevenueContribution || 0
        ),
        type: 'stats',
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">
                {t('Analytics.Sections.Cards.revenueContribution.label')}
              </Text>
              <Text variant="bodySm">
                {t('Analytics.Sections.Cards.revenueContribution.description')}
              </Text>
            </BlockStack>
          </Box>
        ),
        tableRows: [
          [
            {
              label: t('Analytics.Sections.Cards.revenueContribution.totalRevenue'),
              value: null,
            },
            {
              label: null,
              value: currencyFormatter(
                window.shopify.data.shop.currency,
                metrics.totalAmount || 0,
              ),
            },
          ],
          [
            {
              label: t('Analytics.Sections.Cards.revenueContribution.sectionRevenue'),
              value: null,
            },
            {
              label: null,
              value: currencyFormatter(
                window.shopify.data.shop.currency,
                totalRevenue || metrics.totalAmountWithRecommendationPurchases || 0,
              ),
            },
          ],
        ],
        loading,
      },
      {
        label: t('Analytics.Sections.Cards.aov.title'),
        value: currencyFormatter(
          window.shopify.data.shop.currency,
          metrics.aovWithRecommendationPurchases || 0,
        ),
        graphDataSets: [
          {
            label: t('Analytics.Sections.Cards.aov.withSections'),
            data: aggregates?.map((d) => d.aovWithRecommendationPurchases || 0),
          },
        ],
        total: null,
        formatter: (val) =>
          currencyFormatter(window.shopify.data.shop.currency, val),
        type: 'daily',
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">{t('Analytics.Sections.Cards.aov.title')}</Text>
              <Text variant="bodySm">{t('Analytics.Sections.Cards.aov.description')}</Text>
            </BlockStack>
          </Box>
        ),
        graphLabels,
        loading,
      },
      {
        label: t('Analytics.Sections.Cards.checkoutOverTime.label'),
        value: numberFormater(totalPurchases),
        graphDataSets: [
          {
            label: t('Analytics.Sections.Cards.checkoutOverTime.purchases'),
            data: aggregates?.map((d) => d.checkoutCountWithRecommendationPurchases || 0),
          },
        ],
        total: null,
        formatter: numberFormater,
        type: 'daily',
        tooltip: (
          <Box padding={'100'} borderRadius="400">
            <BlockStack gap={'050'}>
              <Text variant="headingSm">
                {t('Analytics.Sections.Cards.checkoutOverTime.label')}
              </Text>
              <Text variant="bodySm">
                {t('Analytics.Sections.Cards.checkoutOverTime.description')}
              </Text>
            </BlockStack>
          </Box>
        ),
        graphLabels,
        loading,
      },
    ]
  }

  function disambiguateLabel(key, value, sections) {
    switch (key) {
      case 'section':
        return (
          'Sections: ' +
          value
            ?.map((id) => {
              const section = sections.find(s => s.id === parseInt(id))
              if (section) {
                return `${section.title || `Section #${section.id}`} (ID: ${section.id})`
              }
              return `ID: ${id}`
            })
            .join(', ')
        )
      default:
        return value
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0
    } else {
      return value === '' || value == null
    }
  }
}

export default AnalyticsSections